// Read layer for the daily set + single questions.
//
// Read contract (verified against iOS FirestoreService.fetchTodayQuestions
// + decodeQuestion, and firestore.rules: both dailySets and questions are
// readable by any authenticated user, incl. anonymous):
//
//   dailySets/{play-day}  → { questionIds: [6 ids], isActive, publishedAt }
//   questions/{id}        → { text, category, publishDate, estimatedResolutionDate,
//                            communityYesPercent, communityNoPercent,
//                            correctAnswer, isResolved, isVoided, ... }
//   users/{uid}/answers/{id} → the user's prior pick (merged in for resume)
//
// NOTE: the dailySet doc ID is the ET-canonical PLAY-DAY string (`playDayString`,
// util/etDate.js) — the set rolls at 3am ET (= midnight PT), NOT ET midnight, so
// this matches iOS `PlayDay` + the server. Keying off plain ET midnight here
// (the old bug) showed PT players the next set 3h early overnight and made the
// server reject the question-share bonus as `not-in-todays-set` in that window.

import { db } from '../firebase.js';
import { playDayString } from '../util/etDate.js';

// ── Today's-six cache (in-memory warm + localStorage cold) ────────────────
// Switching tabs (Today ↔ Results) unmounts the Play screen; without a cache it
// refetches the set + answers on every return, flashing "Loading today's six…".
// The in-memory Map holds live state so a same-session return is instant (and
// skips the refetch). The localStorage mirror survives a FULL reload / PWA cold
// start / new tab — where the Map is gone — so Today paints last-known content
// instantly instead of the loading wall, then revalidates in the background.
//
// Keyed by uid AND the ET date — so it self-invalidates at the midnight-ET
// rollover (a stale entry from yesterday is dropped) and never leaks across
// users. We cache only successful loads — never an error or "no set" — so
// failures always retry.
const todayCache = new Map(); // uid -> { date, questions, submitted }
const TODAY_LS_PREFIX = 'ps_today:';

// JSON with Date preservation — question docs carry publishDate +
// estimatedResolutionDate as Dates. The replacer reads `this[key]` because
// Date.prototype.toJSON has already stringified a Date by the time the replacer
// sees `value`. Mirrors util/screenCache.js's serializer.
function serializeToday(value) {
  return JSON.stringify(value, function reviveDates(key, val) {
    const original = this[key];
    if (original instanceof Date) return { __t: 'date', v: original.toISOString() };
    return val;
  });
}
function deserializeToday(raw) {
  return JSON.parse(raw, (key, val) =>
    val && typeof val === 'object' && val.__t === 'date' ? new Date(val.v) : val
  );
}

/** Warm (in-memory) cached Today for this uid, or null on miss / ET rollover. */
export function getCachedToday(uid) {
  if (!uid) return null;
  const entry = todayCache.get(uid);
  if (!entry) return null;
  if (entry.date !== playDayString()) {
    todayCache.delete(uid); // crossed midnight ET → yesterday's set is stale
    return null;
  }
  return entry;
}

/**
 * Cold (persisted) cached Today — survives a full reload / PWA cold start / new
 * tab, where the in-memory Map is gone. Same ET-date only. The caller paints it
 * instantly (killing the loading wall) but still revalidates in the background;
 * it deliberately does NOT promote into the in-memory cache (that stays the
 * same-session "skip the refetch" path).
 */
export function getPersistedToday(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(TODAY_LS_PREFIX + uid);
    if (!raw) return null;
    const parsed = deserializeToday(raw);
    if (parsed && parsed.date === playDayString() && Array.isArray(parsed.questions)) {
      return parsed;
    }
    localStorage.removeItem(TODAY_LS_PREFIX + uid);
  } catch {
    /* private mode / quota / parse — fall through to a fresh fetch */
  }
  return null;
}

/** Store/refresh the live Today state (current questions + submitted flag), in
 *  memory AND persisted so the next cold load paints instantly. Each question
 *  in `questions` already carries its own `isDoubleDown` flag (set by the
 *  toggle handler / merged in from the answer doc on read) — no separate
 *  double-down field needed here. */
export function setCachedToday(uid, { questions, submitted, headlineImageURL }) {
  if (!uid) return;
  const date = playDayString();
  // The headline is STICKY across re-caches: most callers are answer/DD
  // updates that only know questions+submitted, and losing the hero URL on
  // every tap would make it flicker back in from the network. An explicit
  // value (including null, meaning "today has none") always wins; an
  // omitted one inherits today's prior entry.
  const prior = todayCache.get(uid);
  const entry = {
    date,
    questions,
    submitted,
    headlineImageURL:
      headlineImageURL !== undefined
        ? headlineImageURL
        : prior?.date === date
          ? prior.headlineImageURL ?? null
          : null,
  };
  todayCache.set(uid, entry);
  try {
    localStorage.setItem(TODAY_LS_PREFIX + uid, serializeToday(entry));
  } catch {
    /* private mode / quota — the in-memory cache still serves this session */
  }
}

/** Shape one question doc into the object the UI + write-contract expect. */
function decodeQuestion(id, data) {
  if (!data) return null;
  return {
    id,
    text: data.text ?? '',
    category: data.category ?? 'Markets',
    // Every published question carries an imageURL (admin PublishDailySet
    // hard-blocks publishing without one). External URL, loaded directly.
    imageURL: typeof data.imageURL === 'string' ? data.imageURL : null,
    publishDate: data.publishDate?.toDate?.() ?? new Date(),
    estimatedResolutionDate:
      data.estimatedResolutionDate?.toDate?.() ?? new Date(),
    communityYesPercent:
      typeof data.communityYesPercent === 'number'
        ? data.communityYesPercent
        : 50,
    communityNoPercent:
      typeof data.communityNoPercent === 'number'
        ? data.communityNoPercent
        : 50,
    // The crowd's line frozen at 2 AM ET (snapshotConsensusAtLock) — the pure
    // one-player-one-vote signal the crowd-beat math runs on, never the
    // accuracy-weighted variant. Null until the cron stamps it.
    consensusYesAtLock:
      typeof data.consensusYesAtLock === 'number' ? data.consensusYesAtLock : null,
    correctAnswer:
      typeof data.correctAnswer === 'boolean' ? data.correctAnswer : null,
    isResolved: data.isResolved === true,
    isVoided: data.isVoided === true,
    revealHeadline: data.revealHeadline ?? null,
    // Dig Deeper context (web parity W2/W3, 2026-07-30) — same four fields
    // iOS's decodeQuestion reads. The blurb is the gate: no blurb → no
    // context button, no modal.
    contextBlurb: typeof data.contextBlurb === 'string' && data.contextBlurb ? data.contextBlurb : null,
    contextURL: typeof data.contextURL === 'string' && data.contextURL ? data.contextURL : null,
    contextSourceName: typeof data.contextSourceName === 'string' && data.contextSourceName ? data.contextSourceName : null,
    contextSourceDate: typeof data.contextSourceDate === 'string' && data.contextSourceDate ? data.contextSourceDate : null,
    // MORE ON THIS STORY (news-first N4.3) — the six side of the drill link.
    storyTag: typeof data.storyTag === 'string' && data.storyTag ? data.storyTag : null,
    watchFor: typeof data.watchFor === 'string' && data.watchFor ? data.watchFor : null,
    // "[creator] asks" attribution (Follow mode F3) — { uid, handle, displayName }.
    askedByCreator:
      data.askedByCreator && typeof data.askedByCreator === 'object' && data.askedByCreator.handle
        ? { handle: String(data.askedByCreator.handle), displayName: String(data.askedByCreator.displayName ?? '') }
        : null,
    // user's prior pick, merged by mergeUserAnswers (null until then)
    answer: null,
    isSkipped: false,
    isDoubleDown: false,
    answered: false,
  };
}

/**
 * Fetch today's six. Returns { date, questions } where questions is an
 * ordered array of 6 (or null if no active set for today). Throws on a
 * hard read failure so the caller can show a retry state.
 */
export async function fetchTodaySet() {
  const date = playDayString();
  const setSnap = await db.collection('dailySets').doc(date).get();
  if (!setSnap.exists) return { date, questions: null };

  const setData = setSnap.data();
  const ids = Array.isArray(setData.questionIds) ? setData.questionIds : [];
  if (setData.isActive === false || ids.length !== 6) {
    return { date, questions: null };
  }

  // Batch-fetch the 6 question docs, preserving the set's order.
  const snaps = await Promise.all(
    ids.map((id) => db.collection('questions').doc(id).get())
  );
  const questions = snaps
    .map((s) => (s.exists ? decodeQuestion(s.id, s.data()) : null))
    .filter(Boolean);

  return { date, ...applyLead(setData, questions) };
}

// THE LEAD LEADS (Tom, 2026-08-12) — lockstep with iOS `AppViewModel`.
//
// The Big Picture is no longer a standalone hero that may or may not depict a
// question: it is the art of the day's TENTPOLE, the one question the brief
// called its front-page story (`dailySets.headlineQuestionId`, resolved at
// publish), and that question leads the six for everyone. Only it gets a
// generated image; the other five are text.
//
// Returns the reordered questions AND the hero URL, because the two answers
// come from the same pointer and computing them apart is how they drift.
// The admin's uploaded `headlineImageURL` survives as the FALLBACK — the
// image cron runs hourly, so there is a window after publish where the lead
// has no art yet, and a hand-made picture beats an empty slot.
function applyLead(setData, questions) {
  const leadId =
    typeof setData?.headlineQuestionId === 'string' && setData.headlineQuestionId
      ? setData.headlineQuestionId
      : null;
  const uploaded =
    typeof setData?.headlineImageURL === 'string' && setData.headlineImageURL
      ? setData.headlineImageURL
      : null;
  if (!leadId || !Array.isArray(questions)) {
    return { questions, headlineImageURL: uploaded };
  }
  const i = questions.findIndex((q) => q && q.id === leadId);
  if (i < 0) return { questions, headlineImageURL: uploaded };
  const lead = questions[i];
  const ordered = i === 0 ? questions : [lead, ...questions.filter((_, n) => n !== i)];
  return {
    questions: ordered,
    headlineImageURL: (lead && lead.imageURL) || uploaded,
    headlineQuestionId: leadId,
  };
}

/**
 * Today's six WITH the caller's prior picks merged in — the fast path for the
 * Play screen. After the dailySet read, the 6 question docs AND the 6 answer
 * docs (both keyed by the set's ids) are read in ONE parallel wave, collapsing
 * the old fetchTodaySet()→mergeUserAnswers() two-wave sequence. Returns
 * { date, questions } (questions null if no active set). Throws on a hard read
 * failure so the caller can show a retry state.
 */
export async function fetchTodaySetMerged(uid) {
  const date = playDayString();
  const setSnap = await db.collection('dailySets').doc(date).get();
  if (!setSnap.exists) return { date, questions: null };

  const setData = setSnap.data();
  const ids = Array.isArray(setData.questionIds) ? setData.questionIds : [];
  if (setData.isActive === false || ids.length !== 6) {
    return { date, questions: null };
  }

  // One wave: question docs + the caller's answer docs, in parallel.
  const [qSnaps, aSnaps] = await Promise.all([
    Promise.all(ids.map((id) => db.collection('questions').doc(id).get())),
    uid
      ? Promise.all(
          ids.map((id) =>
            db.collection('users').doc(uid).collection('answers').doc(id).get()
          )
        )
      : Promise.resolve([]),
  ]);

  const questions = ids
    .map((id, i) => {
      const qSnap = qSnaps[i];
      const q = qSnap && qSnap.exists ? decodeQuestion(qSnap.id, qSnap.data()) : null;
      if (!q) return null;
      const aSnap = aSnaps[i];
      if (aSnap && aSnap.exists) {
        const a = aSnap.data();
        q.answer = typeof a.answer === 'boolean' ? a.answer : null;
        q.isSkipped = a.isSkipped === true;
        q.isDoubleDown = a.isDoubleDown === true;
        q.answered = typeof a.answer === 'boolean' || a.isSkipped === true;
      }
      return q;
    })
    .filter(Boolean);

  return { date, ...applyLead(setData, questions) };
}

// Decode a question from the PUBLIC /api/today JSON (dates arrive as ISO strings,
// not Firestore Timestamps). Mirrors decodeQuestion's output shape so the UI +
// write-contract treat a public-painted question identically to an SDK-loaded one.
function decodePublicQuestion(q) {
  if (!q || !q.id) return null;
  return {
    id: q.id,
    text: typeof q.text === 'string' ? q.text : '',
    category: typeof q.category === 'string' ? q.category : 'Markets',
    imageURL: typeof q.imageURL === 'string' ? q.imageURL : null,
    publishDate: q.publishDate ? new Date(q.publishDate) : new Date(),
    estimatedResolutionDate: q.estimatedResolutionDate
      ? new Date(q.estimatedResolutionDate)
      : new Date(),
    communityYesPercent:
      typeof q.communityYesPercent === 'number' ? q.communityYesPercent : 50,
    communityNoPercent:
      typeof q.communityNoPercent === 'number' ? q.communityNoPercent : 50,
    correctAnswer: null, // the public endpoint never exposes an outcome
    isResolved: q.isResolved === true,
    isVoided: q.isVoided === true,
    revealHeadline: q.revealHeadline ?? null,
    contextBlurb: typeof q.contextBlurb === 'string' && q.contextBlurb ? q.contextBlurb : null,
    contextURL: typeof q.contextURL === 'string' && q.contextURL ? q.contextURL : null,
    contextSourceName: typeof q.contextSourceName === 'string' && q.contextSourceName ? q.contextSourceName : null,
    contextSourceDate: typeof q.contextSourceDate === 'string' && q.contextSourceDate ? q.contextSourceDate : null,
    storyTag: typeof q.storyTag === 'string' && q.storyTag ? q.storyTag : null,
    watchFor: typeof q.watchFor === 'string' && q.watchFor ? q.watchFor : null,
    answer: null,
    isSkipped: false,
    isDoubleDown: false,
    answered: false,
  };
}

let _usedTodayPrefetch = false;

/**
 * Fast cold-start paint: today's six from the PUBLIC CDN endpoint (/api/today) —
 * no auth, no App Check, no Firebase SDK round-trip. Returns { date, questions }
 * (UNANSWERED — the per-user picks are merged later by fetchTodaySetMerged once
 * the session is ready), or null on any miss/failure (the caller then falls back
 * to the authenticated SDK load). The first call consumes the in-flight prefetch
 * kicked off in index.html, so the request overlaps the bundle download/parse.
 */
export async function fetchTodaySetPublic() {
  let raw = null;
  try {
    if (!_usedTodayPrefetch && typeof window !== 'undefined' && window.__psTodayPromise) {
      _usedTodayPrefetch = true;
      raw = await window.__psTodayPromise;
    }
    if (!raw) {
      const res = await fetch('/api/today', { headers: { accept: 'application/json' } });
      raw = res.ok ? await res.json() : null;
    }
  } catch {
    return null;
  }
  if (!raw || !Array.isArray(raw.questions)) return null;
  const questions = raw.questions.map(decodePublicQuestion).filter(Boolean);
  if (questions.length !== 6) return null;
  return {
    date: raw.date,
    questions,
    headlineImageURL:
      typeof raw.headlineImageURL === 'string' && raw.headlineImageURL
        ? raw.headlineImageURL
        : null,
  };
}

/** Fetch a single question by id (for /q/<id>). Returns the decoded object or null. */
export async function fetchQuestion(questionId) {
  const snap = await db.collection('questions').doc(questionId).get();
  if (!snap.exists) return null;
  return decodeQuestion(snap.id, snap.data());
}

/**
 * Merge the user's prior answers (if any) into a question array, so a
 * returning anon session resumes where it left off. Reads
 * users/{uid}/answers/{id} for each. Safe to call with no user doc yet
 * (returns the questions unchanged).
 */
export async function mergeUserAnswers(uid, questions) {
  if (!uid || !questions?.length) return questions;
  const snaps = await Promise.all(
    questions.map((q) =>
      db.collection('users').doc(uid).collection('answers').doc(q.id).get()
    )
  );
  return questions.map((q, i) => {
    const s = snaps[i];
    if (!s.exists) return q;
    const a = s.data();
    return {
      ...q,
      answer: typeof a.answer === 'boolean' ? a.answer : null,
      isSkipped: a.isSkipped === true,
      isDoubleDown: a.isDoubleDown === true,
      answered: typeof a.answer === 'boolean' || a.isSkipped === true,
    };
  });
}
